import { useState, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  ChevronLeft,
  ChevronRight,
  CalendarX,
  Clock,
  ArrowRight,
} from "lucide-react";

// --- Helpers & Configuration (Outside the component) ---

const DEFAULT_THEME = {
  bgColor: "bg-gradient-to-br from-slate-300 to-blue-400",
  accentColor: "text-slate-900",
  iconColor: "text-slate-800",
  pattern: "🎉",
  description: "Celebration Day",
};

const HOLIDAY_THEMES = {
  "republic day": {
    bgColor: "bg-gradient-to-br from-orange-300 to-amber-400",
    accentColor: "text-orange-900",
    iconColor: "text-orange-800",
    pattern: "🇮🇳",
    description: "National Pride Day",
  },
  "independence day": {
    bgColor: "bg-gradient-to-br from-orange-300 to-amber-400",
    accentColor: "text-orange-900",
    iconColor: "text-orange-800",
    pattern: "🇮🇳",
    description: "National Pride Day",
  },
  "gandhi jayanti": {
    bgColor: "bg-gradient-to-br from-blue-300 to-indigo-400",
    accentColor: "text-blue-900",
    iconColor: "text-blue-800",
    pattern: "🕊️",
    description: "Day of Non-Violence",
  },
  diwali: {
    bgColor: "bg-gradient-to-br from-yellow-300 to-orange-400",
    accentColor: "text-amber-900",
    iconColor: "text-amber-800",
    pattern: "🪔",
    description: "Festival of Lights",
  },
  holi: {
    bgColor: "bg-gradient-to-br from-pink-300 to-purple-400",
    accentColor: "text-pink-900",
    iconColor: "text-pink-800",
    pattern: "🎨",
    description: "Festival of Colors",
  },
  christmas: {
    bgColor: "bg-gradient-to-br from-red-300 to-green-400",
    accentColor: "text-red-900",
    iconColor: "text-red-800",
    pattern: "🎄",
    description: "Season of Joy",
  },
  eid: {
    bgColor: "bg-gradient-to-br from-emerald-300 to-teal-400",
    accentColor: "text-emerald-900",
    iconColor: "text-emerald-800",
    pattern: "🌙",
    description: "Festival of Breaking Fast",
  },
  dussehra: {
    bgColor: "bg-gradient-to-br from-red-300 to-orange-400",
    accentColor: "text-red-900",
    iconColor: "text-red-800",
    pattern: "🏹",
    description: "Victory of Good over Evil",
  },
  janmashtami: {
    bgColor: "bg-gradient-to-br from-blue-300 to-purple-400",
    accentColor: "text-blue-900",
    iconColor: "text-blue-800",
    pattern: "🪈",
    description: "Divine Birthday",
  },
  onam: {
    bgColor: "bg-gradient-to-br from-yellow-300 to-orange-400",
    accentColor: "text-yellow-900",
    iconColor: "text-yellow-800",
    pattern: "🌺",
    description: "Harvest Festival",
  },
  "new year": {
    bgColor: "bg-gradient-to-br from-purple-300 to-blue-400",
    accentColor: "text-purple-900",
    iconColor: "text-purple-800",
    pattern: "🎆",
    description: "Fresh Beginnings",
  },
};

const getHolidayTheme = (holidayName = "") => {
  const name = holidayName.toLowerCase();
  const themeKey = Object.keys(HOLIDAY_THEMES).find((key) =>
    name.includes(key)
  );
  return themeKey ? HOLIDAY_THEMES[themeKey] : DEFAULT_THEME;
};

const formatDate = (dateString) => {
  if (!dateString) return {};
  const date = new Date(dateString);
  return {
    day: date.getDate(),
    month: date.toLocaleString("en-GB", { month: "short" }).toUpperCase(),
    dayName: date.toLocaleDateString("en-GB", { weekday: "short" }),
    fullMonth: date.toLocaleDateString("en-GB", { month: "long" }),
    year: date.getFullYear(),
  };
};

const calculateDaysRemaining = (dateString) => {
  if (!dateString) return { text: "", status: "" };
  const eventDate = new Date(dateString);
  const today = new Date();
  eventDate.setHours(0, 0, 0, 0);
  today.setHours(0, 0, 0, 0);

  const diff = Math.ceil((eventDate - today) / (1000 * 60 * 60 * 24));

  if (diff === 0) return { text: "Today", status: "today" };
  if (diff === 1) return { text: "Tomorrow", status: "tomorrow" };
  if (diff > 0) return { text: `${diff} days away`, status: "upcoming" };
  return { text: `${Math.abs(diff)} days ago`, status: "past" };
};

const getHolidayTypeStyle = (holidayType) => {
  const type = holidayType?.toUpperCase();
  if (type === "MANDATORY") {
    return {
      bgColor: "bg-blue-100",
      textColor: "text-blue-800",
      label: "Mandatory",
    };
  } else if (type === "OPTIONAL") {
    return {
      bgColor: "bg-amber-100",
      textColor: "text-amber-800",
      label: "Optional",
    };
  }
  return {
    bgColor: "bg-gray-100",
    textColor: "text-gray-800",
    label: "Unknown",
  };
};

const getNextOccurrence = (dateString) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const base = new Date(dateString);

  let next = new Date(today.getFullYear(), base.getMonth(), base.getDate());

  if (next < today) {
    next.setFullYear(today.getFullYear() + 1);
  }

  return next;
};

// --- UI Components ---

const NoHolidays = ({ isEndOfYear = false }) => (
  <div className="flex flex-col items-center justify-center text-center bg-white rounded-2xl border border-gray-100 shadow-sm h-[180px] p-2">
    <div className="flex items-center justify-center w-16 h-16 bg-slate-50 rounded-full border border-slate-200 mb-4">
      <CalendarX className="w-8 h-8 text-slate-400" />
    </div>
    <h3 className="text-lg font-semibold text-slate-700">
      {isEndOfYear ? "All Holidays Completed" : "No Holidays Scheduled"}
    </h3>
    <p className="text-sm text-slate-500">
      {isEndOfYear
        ? "All holidays for this year have ended. New holidays will appear next year!"
        : "Check back later for upcoming holiday updates."}
    </p>
  </div>
);

const HolidaySlider = ({ holidays = [] }) => {
  const navigate = useNavigate();
  const [currentIndex, setCurrentIndex] = useState(0);

  const sortedHolidays = useMemo(() => {
    return holidays
      .map((h) => ({
        ...h,
        nextDate: getNextOccurrence(h.eventDate),
      }))
      .sort((a, b) => a.nextDate - b.nextDate);
  }, [holidays]);

  useEffect(() => {
    setCurrentIndex(0);
  }, [holidays]);

  if (sortedHolidays.length === 0) {
    return <NoHolidays isEndOfYear={false} />;
  }

  const handleNavigation = (direction) => {
    setCurrentIndex((prev) => {
      const newIndex = prev + direction;
      return Math.max(0, Math.min(newIndex, sortedHolidays.length - 1));
    });
  };

  const currentHoliday = sortedHolidays[currentIndex] || {};
  const { holidayName, noOfDays = 1, holidayType } = currentHoliday;

  const theme = getHolidayTheme(holidayName);
  const dateInfo = formatDate(currentHoliday.nextDate);
  const daysRemaining = calculateDaysRemaining(currentHoliday.nextDate);
  const holidayTypeStyle = getHolidayTypeStyle(holidayType);

  const canGoPrev = currentIndex > 0;
  const canGoNext = currentIndex < sortedHolidays.length - 1;

  const navButtonClasses =
    "absolute top-1/2 -translate-y-1/2 p-1 rounded-full shadow-md z-10 transition-all duration-200 border border-slate-200";
  const enabledNavClasses =
    "bg-white hover:bg-gray-50 text-slate-700 hover:scale-105";
  const disabledNavClasses = "bg-slate-100 text-slate-400 cursor-not-allowed";

  return (
    <div className="relative w-full h-full bg-white rounded-2xl border border-slate-200 shadow-sm">
      <button
        onClick={() => handleNavigation(-1)}
        disabled={!canGoPrev}
        className={`${navButtonClasses} left-2 ${
          canGoPrev ? enabledNavClasses : disabledNavClasses
        }`}
      >
        <ChevronLeft className="w-5 h-5" />
      </button>
      <button
        onClick={() => handleNavigation(1)}
        disabled={!canGoNext}
        className={`${navButtonClasses} right-2 ${
          canGoNext ? enabledNavClasses : disabledNavClasses
        }`}
      >
        <ChevronRight className="w-5 h-5" />
      </button>

      <div
        className={`relative h-full w-full rounded-2xl overflow-hidden px-6 py-4 flex flex-col justify-between ${theme.bgColor} ${theme.accentColor}`}
      >
        <div className="absolute top-1/2 right-6 -translate-y-1/2 text-6xl opacity-80 select-none">
          {theme.pattern}
        </div>

        <header className="flex justify-between items-start w-full">
          <div className="flex items-center gap-4">
            <div className="flex flex-col items-center justify-center bg-white/80 backdrop-blur-sm rounded-lg p-2 shadow-md min-w-[70px]">
              <span className="text-2xl font-bold text-slate-800 leading-none">
                {dateInfo.day}
              </span>
              <span className="text-sm font-semibold text-slate-600">
                {dateInfo.month}
              </span>
              <span className="text-xs text-slate-500">{dateInfo.dayName}</span>
            </div>
            <div>
              <h3 className="text-xl font-bold text-slate-900">
                {holidayName}
              </h3>
              <p className="text-sm font-semibold">{theme.description}</p>
            </div>
          </div>
          <button
            onClick={() => navigate("/ess/list-of-holidays")}
            className="group flex items-center whitespace-nowrap space-x-3 p-1 text-slate-800 text-sm text-black font-semibold rounded-full transform hover:-translate-y-0.5 transition-all duration-200"
          >
            <span>View All</span>
            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </button>
        </header>

        <footer className="flex items-center justify-between w-full text-slate-800">
          <div className="flex items-center gap-2">
            <div
              className={`px-3 py-1 text-sm font-semibold rounded-full ${
                daysRemaining.status === "past"
                  ? "bg-red-100 text-red-800"
                  : daysRemaining.status === "today"
                  ? "bg-green-100 text-green-800"
                  : daysRemaining.status === "tomorrow"
                  ? "bg-blue-100 text-blue-800"
                  : "bg-purple-100 text-purple-800"
              }`}
            >
              {daysRemaining.text}
            </div>
            <div
              className={`px-3 py-1 text-xs font-semibold rounded-full ${holidayTypeStyle.bgColor} ${holidayTypeStyle.textColor}`}
            >
              {holidayTypeStyle.label}
            </div>
          </div>
          <div className="flex items-center gap-2 text-sm font-medium">
            <Clock className={`w-4 h-4 ${theme.iconColor}`} />
            <span>
              {noOfDays} day{noOfDays > 1 ? "s" : ""}
            </span>
          </div>
        </footer>
      </div>
    </div>
  );
};

export default HolidaySlider;
